(function() {
  'use strict';

  /* ========================================
     UTILITY FUNCTIONS
     ======================================== */

  function formatMoney(cents) {
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return 'R$ ' + value.replace(/,00$/, '');
  }

  function slugify(str) {
    return str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '-');
  }

  function dispatchCustomEvent(eventName, data) {
    document.dispatchEvent(new CustomEvent(eventName, {
      detail: data,
      bubbles: true
    }));
  }

  /* ========================================
     PRINT SETS PAGE - Navigation
     ======================================== */

  class PrintSetsPage extends HTMLElement {
    constructor() {
      super();
      this.listing = this.querySelector('.ps-listing');
      this.details = this.querySelectorAll('print-set-detail');
      this.currentDetail = null;

      this.init();
    }

    init() {
      // Handle card clicks
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-print-set-link]');
        if (link) {
          e.preventDefault();
          const handle = link.dataset.printSetLink;
          this.showDetail(handle);
          window.history.pushState({ setHandle: handle }, '', '?set=' + handle);
        }

        const backBtn = e.target.closest('[data-print-set-back]');
        if (backBtn) {
          e.preventDefault();
          this.showListing();
          window.history.pushState({}, '', window.location.pathname);
        }
      });

      // Handle browser back/forward
      window.addEventListener('popstate', (e) => {
        if (e.state && e.state.setHandle) {
          this.showDetail(e.state.setHandle, false);
        } else {
          this.showListing(false);
        }
      });

      // Check URL on load
      const params = new URLSearchParams(window.location.search);
      const setHandle = params.get('set');
      if (setHandle) {
        this.showDetail(setHandle, false);
      }

      // Init infinite scroll
      this.initInfiniteScroll();
    }

    showDetail(handle, scroll) {
      if (scroll !== false) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      this.listing.style.display = 'none';

      this.details.forEach(detail => {
        if (detail.dataset.setHandle === handle) {
          detail.style.display = 'block';
          detail.initDetail();
          this.currentDetail = detail;
        } else {
          detail.style.display = 'none';
        }
      });
    }

    showListing(scroll) {
      if (scroll !== false) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      this.listing.style.display = 'block';

      this.details.forEach(detail => {
        detail.style.display = 'none';
      });

      this.currentDetail = null;
    }

    initInfiniteScroll() {
      const grids = this.querySelectorAll('.ps-grid[id]');

      grids.forEach(grid => {
        const sectionId = grid.id.replace('PrintSetsGrid-', '');
        const itemsPerScroll = parseInt(grid.dataset.itemsPerScroll) || 8;
        const loader = document.getElementById('PSInfiniteLoader-' + sectionId);
        const sentinel = document.getElementById('PSSentinel-' + sectionId);

        if (!loader || !sentinel) return;

        const hiddenSelector = '.ps-card.hidden-item';
        let isLoading = false;

        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !isLoading) {
              loadMore();
            }
          });
        }, { rootMargin: '200px', threshold: 0.1 });

        if (grid.querySelectorAll(hiddenSelector).length > 0) {
          observer.observe(sentinel);
        } else {
          sentinel.style.display = 'none';
        }

        function loadMore() {
          const hidden = grid.querySelectorAll(hiddenSelector);
          if (hidden.length === 0) {
            observer.disconnect();
            sentinel.remove();
            loader.remove();
            return;
          }

          isLoading = true;
          loader.style.display = 'flex';

          setTimeout(() => {
            for (let i = 0; i < itemsPerScroll && hidden[i]; i++) {
              hidden[i].style.display = '';
              hidden[i].classList.remove('hidden-item');
              hidden[i].classList.add('fade-in');
            }

            isLoading = false;
            loader.style.display = 'none';

            if (grid.querySelectorAll(hiddenSelector).length === 0) {
              observer.disconnect();
              sentinel.remove();
              loader.remove();
            }
          }, 500);
        }
      });
    }
  }

  /* ========================================
     PRINT SET DETAIL - Acabamento & Cart
     ======================================== */

  class PrintSetDetail extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.variantsData = null;
      this.selectedTipo = 'canvas';
      this.selectedCor = 'mel';
      this.lastColorBeforeNoFrame = 'mel';
    }

    initDetail() {
      if (this.initialized) return;
      this.initialized = true;

      // Parse variant data
      const dataScript = this.querySelector('script[data-print-set-variants]');
      if (dataScript) {
        try {
          this.variantsData = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('Error parsing print set variants:', e);
          return;
        }
      }

      // Apply layout positions from JSON config
      this.applyLayoutPositions();

      this.tipoFieldset = this.querySelector('[data-option="tipo"]');
      this.corFieldset = this.querySelector('[data-option="cor"]');
      this.addAllBtn = this.querySelector('[data-add-all]');
      this.errorEl = this.querySelector('.ps-detail__error');
      this.priceEl = this.querySelector('.ps-detail__price-amount');
      this.pixEl = this.querySelector('.ps-detail__pix-value');
      this.pixDiscount = parseInt(this.dataset.pixDiscount) || 10;
      this.maxInstallments = parseInt(this.dataset.maxInstallments) || 12;

      this.bindEvents();
      this.updateDestaques();
      this.resolveVariants();
    }

    bindEvents() {
      // Tipo de moldura change
      if (this.tipoFieldset) {
        this.tipoFieldset.querySelectorAll('input[type="radio"]').forEach(input => {
          input.addEventListener('change', () => {
            this.selectedTipo = input.value;
            this.tipoFieldset.querySelector('.form__label__value').textContent = input.value;
            this.handleTipoChange();
            this.updateDestaques();
            this.resolveVariants();
          });
        });
      }

      // Cor da moldura change
      if (this.corFieldset) {
        this.corFieldset.querySelectorAll('input[type="radio"]').forEach(input => {
          input.addEventListener('change', () => {
            this.selectedCor = input.value;
            this.corFieldset.querySelector('.form__label__value').textContent = input.value;
            this.resolveVariants();
          });
        });
      }

      // Add all to cart
      if (this.addAllBtn) {
        this.addAllBtn.addEventListener('click', () => {
          this.addAllToCart();
        });
      }

      // Environment carousel dots
      const dots = this.querySelectorAll('.ps-detail__env-dot');
      dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const slideIndex = dot.dataset.slide;
          this.querySelectorAll('.ps-detail__env-slide').forEach(s => s.classList.remove('active'));
          this.querySelectorAll('.ps-detail__env-dot').forEach(d => d.classList.remove('active'));
          const targetSlide = this.querySelector('.ps-detail__env-slide[data-slide="' + slideIndex + '"]');
          if (targetSlide) targetSlide.classList.add('active');
          dot.classList.add('active');
        });
      });
    }

    handleTipoChange() {
      if (this.selectedTipo === 'sem moldura') {
        // Save current color
        const currentColor = this.corFieldset.querySelector('input[type="radio"]:checked');
        if (currentColor && currentColor.value !== 'sem moldura') {
          this.lastColorBeforeNoFrame = currentColor.value;
        }

        // Disable color options
        this.corFieldset.classList.add('disable');
        this.corFieldset.querySelectorAll('input[type="radio"]').forEach(input => {
          input.disabled = true;
        });

        // Set color to match "sem moldura"
        this.selectedCor = 'sem moldura';
        this.corFieldset.querySelector('.form__label__value').textContent = 'sem moldura';
      } else {
        // Re-enable color options
        this.corFieldset.classList.remove('disable');
        this.corFieldset.querySelectorAll('input[type="radio"]').forEach(input => {
          input.disabled = false;
        });

        // Restore last color
        const restoreInput = this.corFieldset.querySelector('input[value="' + this.lastColorBeforeNoFrame + '"]');
        if (restoreInput) {
          restoreInput.checked = true;
          this.selectedCor = this.lastColorBeforeNoFrame;
          this.corFieldset.querySelector('.form__label__value').textContent = this.lastColorBeforeNoFrame;
        }
      }
    }

    updateDestaques() {
      const handleMap = {
        'padrão': 'padrao',
        'padrão com paspatur': 'padrao-com-paspatur',
        'caixa': 'caixa',
        'canvas': 'canvas',
        'sem moldura': 'sem-moldura'
      };

      const tipoHandle = handleMap[this.selectedTipo] || slugify(this.selectedTipo);

      this.querySelectorAll('.ps-destaque').forEach(el => {
        if (el.classList.contains('destaque__' + tipoHandle)) {
          el.classList.add('destaque--visible');
          el.classList.remove('destaque--hidden');
        } else {
          el.classList.add('destaque--hidden');
          el.classList.remove('destaque--visible');
        }
      });
    }

    resolveVariants() {
      if (!this.variantsData) return;

      let totalPrice = 0;
      let allAvailable = true;
      this.resolvedItems = [];

      this.variantsData.products.forEach(productData => {
        const targetSize = productData.variantSize;
        const targetTipo = this.selectedTipo;
        const targetCor = this.selectedCor;

        // Find matching variant
        const match = productData.allVariants.find(v => {
          return v.options[0] === targetTipo &&
                 v.options[1] === targetCor &&
                 v.options[2] === targetSize;
        });

        if (match && match.available) {
          totalPrice += match.price;
          this.resolvedItems.push({
            variantId: match.id,
            price: match.price,
            handle: productData.handle,
            title: productData.title
          });
        } else if (match) {
          // Variant exists but unavailable
          totalPrice += match.price;
          allAvailable = false;
          this.resolvedItems.push({
            variantId: match.id,
            price: match.price,
            handle: productData.handle,
            title: productData.title,
            unavailable: true
          });
        } else {
          // No matching variant found
          allAvailable = false;
          this.resolvedItems.push({
            variantId: null,
            price: 0,
            handle: productData.handle,
            title: productData.title,
            unavailable: true
          });
        }

        // Update individual price display
        const priceEl = this.querySelector('.ps-detail__product-price[data-product-handle="' + productData.handle + '"]');
        if (priceEl && match) {
          priceEl.textContent = formatMoney(match.price);
        }
      });

      // Update total price
      if (this.priceEl) {
        this.priceEl.textContent = formatMoney(totalPrice);
        this.priceEl.dataset.totalPrice = totalPrice;
      }

      // Update PIX price
      if (this.pixEl) {
        const pixTotal = Math.round(totalPrice * (100 - this.pixDiscount) / 100);
        this.pixEl.textContent = formatMoney(pixTotal);
      }

      // Update add to cart button state
      if (this.addAllBtn) {
        const unavailableCount = this.resolvedItems.filter(i => i.unavailable).length;
        if (unavailableCount === this.resolvedItems.length) {
          this.addAllBtn.disabled = true;
          this.addAllBtn.querySelector('.ps-detail__add-all-text').textContent = 'INDISPONIVEL';
        } else if (unavailableCount > 0) {
          this.addAllBtn.disabled = false;
          this.addAllBtn.querySelector('.ps-detail__add-all-text').innerHTML =
            '<svg width="18" height="18" viewBox="0 0 16 15.568" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path d="M14.703 3.702h-3.065a3.7 3.7 0 0 0-1.077-2.618A3.68 3.68 0 0 0 7.964 0a3.68 3.68 0 0 0-2.599 1.085 3.7 3.7 0 0 0-1.077 2.618H1.226a1.24 1.24 0 0 0-.865.362A1.24 1.24 0 0 0 0 4.938v9.257c0 .327.129.642.358.873a1.24 1.24 0 0 0 .866.362h13.478a1.24 1.24 0 0 0 .866-.362 1.24 1.24 0 0 0 .358-.873V4.938a1.24 1.24 0 0 0-.358-.873 1.24 1.24 0 0 0-.866-.362M7.964 1.234c.65 0 1.274.26 1.732.723a2.5 2.5 0 0 1 .718 1.745h-4.9a2.5 2.5 0 0 1 .718-1.745 2.45 2.45 0 0 1 1.732-.723m6.739 12.96H1.226V4.938h3.062v1.234c0 .163.064.32.179.437a.6.6 0 0 0 .434.181.6.6 0 0 0 .434-.181.63.63 0 0 0 .179-.437V4.938h4.901v1.234c0 .163.064.32.179.437a.6.6 0 0 0 .434.181.6.6 0 0 0 .434-.181.63.63 0 0 0 .179-.437V4.938h3.065z"/></svg> ADICIONAR DISPONIVEIS AO CARRINHO';
        } else {
          this.addAllBtn.disabled = false;
          this.addAllBtn.querySelector('.ps-detail__add-all-text').innerHTML =
            '<svg width="18" height="18" viewBox="0 0 16 15.568" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path d="M14.703 3.702h-3.065a3.7 3.7 0 0 0-1.077-2.618A3.68 3.68 0 0 0 7.964 0a3.68 3.68 0 0 0-2.599 1.085 3.7 3.7 0 0 0-1.077 2.618H1.226a1.24 1.24 0 0 0-.865.362A1.24 1.24 0 0 0 0 4.938v9.257c0 .327.129.642.358.873a1.24 1.24 0 0 0 .866.362h13.478a1.24 1.24 0 0 0 .866-.362 1.24 1.24 0 0 0 .358-.873V4.938a1.24 1.24 0 0 0-.358-.873 1.24 1.24 0 0 0-.866-.362M7.964 1.234c.65 0 1.274.26 1.732.723a2.5 2.5 0 0 1 .718 1.745h-4.9a2.5 2.5 0 0 1 .718-1.745 2.45 2.45 0 0 1 1.732-.723m6.739 12.96H1.226V4.938h3.062v1.234c0 .163.064.32.179.437a.6.6 0 0 0 .434.181.6.6 0 0 0 .434-.181.63.63 0 0 0 .179-.437V4.938h4.901v1.234c0 .163.064.32.179.437a.6.6 0 0 0 .434.181.6.6 0 0 0 .434-.181.63.63 0 0 0 .179-.437V4.938h3.065z"/></svg> ADICIONAR TODOS AO CARRINHO';
        }

        // Show error for unavailable items
        if (this.errorEl) {
          if (unavailableCount > 0) {
            const names = this.resolvedItems
              .filter(i => i.unavailable)
              .map(i => i.title)
              .join(', ');
            this.errorEl.textContent = 'Indisponivel neste acabamento: ' + names;
            this.errorEl.style.display = 'block';
          } else {
            this.errorEl.style.display = 'none';
          }
        }
      }
    }

    applyLayoutPositions() {
      // Read layout config from data attribute or embedded JSON
      if (!this.variantsData || !this.variantsData.layout) return;

      try {
        const config = JSON.parse(this.variantsData.layout);
        if (!Array.isArray(config)) return;

        // Apply positions to all canvas frames in this detail view
        const canvasFrames = this.querySelectorAll('.ps-frame-abs');
        config.forEach((item, index) => {
          if (canvasFrames[index]) {
            canvasFrames[index].style.left = item.x + '%';
            canvasFrames[index].style.top = item.y + '%';
          }
        });

        // Also apply to card preview canvases
      } catch (e) {
        // Layout is a predefined name, not JSON - ignore
      }
    }

    async addAllToCart() {
      const availableItems = this.resolvedItems.filter(i => !i.unavailable && i.variantId);

      if (availableItems.length === 0) return;

      this.addAllBtn.classList.add('loading');
      this.addAllBtn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: availableItems.map(item => ({
              id: item.variantId,
              quantity: 1
            }))
          })
        });

        if (!response.ok) {
          throw new Error('Erro ao adicionar ao carrinho');
        }

        // Fetch updated cart sections
        const sectionsResponse = await fetch('/cart.js?sections=cart-drawer,cart-bubble');
        const sectionsData = await sectionsResponse.json();

        // Update cart drawer HTML
        if (sectionsData.sections) {
          Object.entries(sectionsData.sections).forEach(([sectionName, html]) => {
            const targetEl = document.getElementById(sectionName === 'cart-drawer' ? 'Cart-Drawer' : 'cart-bubble');
            if (targetEl) {
              const temp = document.createElement('div');
              temp.innerHTML = html;
              const newContent = temp.querySelector('#' + targetEl.id);
              if (newContent) {
                targetEl.innerHTML = newContent.innerHTML;
              }
            }
          });
        }

        // Open cart drawer
        const cartDrawer = document.getElementById('Cart-Drawer');
        if (cartDrawer) {
          cartDrawer.classList.add('active');
          document.body.classList.add('open-cc', 'open-cart');
        }

        dispatchCustomEvent('cart:item-added', {
          items: availableItems
        });

      } catch (error) {
        console.error('Add to cart error:', error);
        if (this.errorEl) {
          this.errorEl.textContent = 'Erro ao adicionar ao carrinho. Tente novamente.';
          this.errorEl.style.display = 'block';
          setTimeout(() => {
            this.errorEl.style.display = 'none';
          }, 5000);
        }
      } finally {
        this.addAllBtn.classList.remove('loading');
        this.addAllBtn.disabled = false;
      }
    }
  }

  /* ========================================
     REGISTER CUSTOM ELEMENTS
     ======================================== */

  if (!customElements.get('print-sets-page')) {
    customElements.define('print-sets-page', PrintSetsPage);
  }

  if (!customElements.get('print-set-detail')) {
    customElements.define('print-set-detail', PrintSetDetail);
  }

})();
